import React from 'react';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import { 
  TreePine, 
  BarChart3, 
  Satellite, 
  FileCheck, 
  Sprout, 
  ArrowRightLeft
} from 'lucide-react';

const Services = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const services = [
    {
      icon: TreePine,
      title: 'Proyectos Forestales',
      description: 'Reforestación y conservación de bosques nativos para generación de créditos de carbono verificados bajo estándares VCS y Gold Standard.',
      color: 'var(--primary-500)',
    },
    {
      icon: BarChart3,
      title: 'Consultoría en Carbono',
      description: 'Asesoría estratégica para empresas en su camino hacia la neutralidad de carbono. Inventarios de GEI y planes de reducción.',
      color: 'var(--accent-400)',
    },
    {
      icon: Satellite,
      title: 'Monitoreo Satelital',
      description: 'Tecnología de teledetección para seguimiento en tiempo real del crecimiento forestal, salud del ecosistema y captura de CO₂.',
      color: '#60a5fa',
    },
    {
      icon: FileCheck,
      title: 'Validación y Verificación',
      description: 'Proceso completo de validación de proyectos ante organismos internacionales y verificación periódica de resultados.',
      color: '#a78bfa',
    },
    {
      icon: Sprout,
      title: 'Agricultura Regenerativa',
      description: 'Implementación de prácticas agrícolas que capturan carbono en el suelo, mejorando la productividad y la resiliencia climática.',
      color: '#34d399',
    },
    {
      icon: ArrowRightLeft,
      title: 'Comercialización de Créditos',
      description: 'Acceso al mercado internacional de carbono. Conectamos proyectos locales con compradores corporativos globales.',
      color: '#fbbf24',
    },
  ];

  return (
    <section id="servicios" style={{
      background: 'var(--dark)',
      padding: 'var(--space-3xl) 0',
      position: 'relative',
    }}>
      <div className="container" ref={ref}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 80px' }}
        >
          <span style={{
            display: 'inline-block',
            padding: '8px 20px',
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            borderRadius: '50px',
            color: 'var(--primary-400)',
            fontSize: '0.85rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '2px',
            marginBottom: '20px',
          }}>
            Nuestros Servicios
          </span>
          <h2 style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            marginBottom: '20px',
          }}>
            Soluciones Integrales de{' '}
            <span className="text-gradient">Carbono</span>
          </h2>
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '1.1rem',
            lineHeight: 1.7,
          }}>
            Ofrecemos un portafolio completo de servicios para empresas, gobiernos 
            y organizaciones que buscan liderar la transición hacia una economía baja en carbono.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
          gap: '24px',
        }}>
          {services.map((service, i) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              style={{
                background: 'var(--dark-800)',
                borderRadius: '20px',
                padding: '40px',
                border: '1px solid var(--border)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.4s ease',
                cursor: 'default',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.3)';
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = 'var(--shadow-xl)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Top accent line */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: service.color,
                opacity: 0.6,
                transform: 'scaleX(0)',
                transformOrigin: 'left',
                transition: 'transform 0.4s ease',
              }} className="service-accent" />

              <div style={{
                width: '64px',
                height: '64px',
                background: `${service.color}15`,
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '24px',
                transition: 'all 0.3s ease',
              }} className="service-icon">
                <service.icon size={28} style={{ color: service.color }} />
              </div>

              <h3 style={{
                fontSize: '1.3rem',
                fontWeight: 600,
                marginBottom: '12px',
              }}>
                {service.title}
              </h3>

              <p style={{
                color: 'var(--text-muted)',
                lineHeight: 1.7,
                fontSize: '0.95rem',
              }}>
                {service.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      <style>{`
        .service-accent {
          transform: scaleX(0) !important;
        }
        div:hover > .service-accent {
          transform: scaleX(1) !important;
        }
        div:hover > .service-icon {
          background: var(--primary-600) !important;
        }
        div:hover > .service-icon svg {
          color: white !important;
        }
      `}</style>
    </section>
  );
};

export default Services;
